import React, { useEffect, useRef, useContext, useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import { AuthContext, getPreferredLoginPath } from '../contexts/AuthContext';
import { useIsMobile } from './useMediaQuery';

declare global {
  interface Window {
    __kindredlyIdleTimeoutMs?: number;
    __kindredlyGracePeriodMs?: number;
  }
}

/**
 * HIPAA-compliant idle timeout.
 *
 * After {@link IDLE_TIMEOUT_MS} of no user activity a modal dialog is shown
 * asking "Are you still working?" with a countdown timer. If the user does not
 * interact within {@link GRACE_PERIOD_MS} the session is logged out and the
 * browser is redirected to /login — ensuring PHI is not visible on an
 * unattended screen.
 *
 * Activity is tracked via mousemove, keydown, mousedown, touchstart, scroll,
 * and focus events.  The handler is throttled to fire at most once per
 * {@link THROTTLE_MS} to avoid performance overhead.
 *
 * Cross-tab awareness: a localStorage key is bumped on every activity event so
 * other tabs can piggyback on the same "last active" timestamp.
 */

const IDLE_TIMEOUT_MS =
  (typeof window !== 'undefined' && window.__kindredlyIdleTimeoutMs) ||
  15 * 60 * 1000; // 15 minutes
const GRACE_PERIOD_MS =
  (typeof window !== 'undefined' && window.__kindredlyGracePeriodMs) ||
  5 * 60 * 1000; // 5 minutes after warning
const GRACE_SECONDS = Math.floor(GRACE_PERIOD_MS / 1000);
const THROTTLE_MS = 10_000; // throttle activity events
const LAST_ACTIVE_KEY = 'idle:lastActive';
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousemove',
  'keydown',
  'mousedown',
  'touchstart',
  'scroll',
  'focus',
];

export function useIdleTimeout(): React.ReactNode {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const logout = authContext?.logout;
  const isMobile = useIsMobile();

  const lastActiveRef = useRef(Date.now());
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const throttleRef = useRef(0);
  const dialogOpenRef = useRef(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(GRACE_SECONDS);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const dismissWarning = useCallback(() => {
    dialogOpenRef.current = false;
    setDialogOpen(false);
    setSecondsLeft(GRACE_SECONDS);
    clearTimers();
  }, [clearTimers]);

  const scheduleWarning = useCallback(() => {
    clearTimers();
    dialogOpenRef.current = false;
    setDialogOpen(false);
    setSecondsLeft(GRACE_SECONDS);

    warningTimerRef.current = setTimeout(() => {
      setSecondsLeft(GRACE_SECONDS);
      dialogOpenRef.current = true;
      setDialogOpen(true);

      countdownRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            if (countdownRef.current) clearInterval(countdownRef.current);
            countdownRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      logoutTimerRef.current = setTimeout(() => {
        // Keep the dialog open during logout so PHI stays hidden
        // behind the backdrop blur until the redirect completes.
        if (logout) {
          logout(`${getPreferredLoginPath()}?reason=idle_timeout`);
        }
      }, GRACE_PERIOD_MS);
    }, IDLE_TIMEOUT_MS);
  }, [clearTimers, logout]);

  const handleStaySignedIn = useCallback(() => {
    dismissWarning();
    lastActiveRef.current = Date.now();
    try {
      localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
    } catch {
      /* storage full / unavailable */
    }
    scheduleWarning();
  }, [dismissWarning, scheduleWarning]);

  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - throttleRef.current < THROTTLE_MS) return;
    throttleRef.current = now;

    // While the dialog is open, ignore all activity — the user must
    // explicitly click "Yes, keep me signed in" to acknowledge. We also
    // skip the localStorage write so other tabs don't see phantom
    // activity and silently dismiss their own warning dialogs.
    if (dialogOpenRef.current) return;

    lastActiveRef.current = now;
    try {
      localStorage.setItem(LAST_ACTIVE_KEY, String(now));
    } catch {
      /* storage full / unavailable */
    }

    scheduleWarning();
  }, [scheduleWarning]);

  useEffect(() => {
    if (!user) {
      clearTimers();
      dialogOpenRef.current = false;
      setDialogOpen(false);
      return;
    }

    // Check if another tab recorded more recent activity
    const storedActive = Number(localStorage.getItem(LAST_ACTIVE_KEY));
    if (storedActive && storedActive > lastActiveRef.current) {
      lastActiveRef.current = storedActive;
    }

    scheduleWarning();

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, handleActivity, { passive: true });
    }

    // Cross-tab: when another tab bumps lastActive, reset our timers
    const onStorage = (e: StorageEvent) => {
      if (e.key === LAST_ACTIVE_KEY && e.newValue) {
        const otherTabActive = Number(e.newValue);
        if (otherTabActive > lastActiveRef.current) {
          lastActiveRef.current = otherTabActive;
          dismissWarning();
          scheduleWarning();
        }
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      clearTimers();
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, handleActivity);
      }
      window.removeEventListener('storage', onStorage);
    };
  }, [user, handleActivity, scheduleWarning, clearTimers, dismissWarning]);

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const progressPct = (secondsLeft / GRACE_SECONDS) * 100;

  return (
    <Dialog
      open={dialogOpen}
      fullScreen={isMobile}
      maxWidth="xs"
      fullWidth
      aria-labelledby="idle-timeout-dialog-title"
      slotProps={{
        backdrop: { sx: { backdropFilter: 'blur(2px)' } },
      }}
    >
      <DialogTitle id="idle-timeout-dialog-title" sx={{ pb: 1 }}>
        Are you still working?
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Your session will be logged out due to inactivity to protect patient
          information.
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h4"
            component="div"
            sx={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums', mb: 1 }}
          >
            {minutes}:{seconds.toString().padStart(2, '0')}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressPct}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Click the button below to stay signed in.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleStaySignedIn}
          autoFocus
        >
          Yes, keep me signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
}
